import { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactElement } from 'react';
import styled, { css } from "styled-components";

type ButtonProps = {
  text: string | ReactElement;
  title?: string;
  handleClick?: () => void;
  textColor?: string;
  backgroundColor?: string;
  callToAction?: boolean;
  underlineOnHover?: boolean;
  type?: "button" | "submit" | "reset";
  style?: any;
  [rest: string]: any;
};

export function Button({
  text,
  handleClick,
  textColor = "var(--color-blue)",
  backgroundColor = "none",
  callToAction,
  underlineOnHover,
  title,
  type,
  style,
  ...rest
}: ButtonProps) {
  return (
    <StyledButton
      onClick={handleClick}
      title={text.toString()}
      type={type}
      style={style}
      $textColor={textColor}
      $backgroundColor={backgroundColor}
      $callToAction={callToAction}
      $underlineOnHover={underlineOnHover}
      {...rest}
    >
      {text}
    </StyledButton>
  )
}

type AnchorProps = {
  id?: string;
  text?: string | ReactElement;
  title?: string;
  href: string;
  textColor?: string;
  backgroundColor?: string;
  callToAction?: boolean;
  underlineOnHover?: boolean;
  children?: React.ReactNode;
  [rest: string]: any;
};

export function Anchor({
  id,
  text,
  title,
  textColor = "var(--color-blue)",
  backgroundColor = "none",
  callToAction,
  underlineOnHover,
  href,
  children,
  ...rest
}: AnchorProps) {
  return (
      <StyledAnchor
          id={id}
          href={href}
          title={text?.toString() ?? title!}
          $textColor={textColor}
          $backgroundColor={backgroundColor}
          $callToAction={callToAction}
          $underlineOnHover={underlineOnHover}
          {...rest}
      >
          {text ?? children}
      </StyledAnchor>
  )
}

type StyledButtonProps = IButton & ButtonHTMLAttributes<HTMLButtonElement>;
type StyledAnchorProps = IButton & AnchorHTMLAttributes<HTMLAnchorElement>;

interface IButton {
  $textColor: string;
  $backgroundColor: string;
  $callToAction?: boolean;
  $underlineOnHover?: boolean;
}

const buttonStyles = css<IButton>`
  background: ${props => props.$backgroundColor};
  color: ${props => props.$textColor};
  border: none;
  border-radius: var(--radius);
  outline: inherit;
  display: flex;
  text-align: center;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: ${props => props.$callToAction ? "30px 35px" : "10px"};
  font-family: var(--heading-font);
  font-size: ${props => props.$callToAction ? "clamp(1rem, -2.3149rem + 8.8398vw, 1.5rem)" : "1rem"};
  cursor: pointer;
  text-decoration: none;
  
  &::before {
    display: block;
    content: attr(title);
    font-weight: bold;
    height: 0;
    overflow: hidden;
    visibility: hidden;
  }
  &:focus, &:focus-visible {
    border: medium none;
    outline: auto;
  }
  &:hover {
    font-weight: 600;
    text-decoration: ${props => props.$underlineOnHover ? 'underline' : 'none'};
  }
  &:disabled, &[disabled]{
    pointer-events: none;
    font-weight: 600;
    &:hover {
      font-weight: 600;
      text-decoration: none;
    }
  }
  
  @media (max-width: 1200px) {
    padding: ${props => props.$callToAction ? "30px 35px" : "6px"};
  }
  
  @media (max-width: 750px) {
   padding: ${props => props.$callToAction ? "10px" : "6px"};
  }
`;

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonStyles}
`;

export const PrimaryButton = styled.button<StyledButtonProps>`
  ${buttonStyles}
  &:disabled, &[disabled]{
    opacity: 0.4;
  }
`;

const StyledAnchor = styled.a<StyledAnchorProps>`
  ${buttonStyles}
`;